import { Alert, Box, Button, Group, Stack, Text } from "@mantine/core";
import { IconNotes } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import type { FeePolicy } from "../../apis/tenantFeePolicy";
import { formatMoney, formatPercent } from "../../utils/format";
import { calcMethodLabel, scopeLabel } from "../../utils/fee-policy-labels";

interface Props {
  /** DRAFT policies for this tenant. */
  drafts: FeePolicy[];
  /** Called after a draft is canceled so the parent can refetch. */
  onCanceled: () => void;
}

/** Short summary chip text: "1.00%" / "5,000₮" / method label. */
function summary(p: FeePolicy): string {
  if (p.calculationMethod === "PERCENTAGE" && p.feeRate) return formatPercent(Number(p.feeRate) * 100);
  if (p.calculationMethod === "FLAT" && p.flatFeeAmount) return formatMoney(p.flatFeeAmount, p.feeCurrency);
  return calcMethodLabel(p.calculationMethod);
}

/**
 * Spec §2.1 — secondary amber strip listing unfinished drafts. Lighter visual
 * weight than the active DEFAULT/NAMED cards; hidden entirely when no drafts.
 * Each chip opens the draft straight in edit mode.
 */
export function PolicyDraftListCard({ drafts }: Props) {
  const navigate = useNavigate();
  if (drafts.length === 0) return null;

  return (
    <Alert color="yellow" variant="light" icon={<IconNotes size={16} />} radius="md">
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
        <Stack gap={2}>
          <Text fw={600} size="sm">Дуусгаагүй {drafts.length} ноорог байна</Text>
          <Text size="xs" c="dimmed">Идэвхжүүлээгүй. Үргэлжлүүлэн засаад идэвхжүүлнэ.</Text>
        </Stack>
        <Group gap="xs" wrap="wrap">
          {drafts.map((p) => {
            const label = p.scope === "NAMED"
              ? `${scopeLabel("NAMED")} · ${p.name ?? "(нэргүй)"}`
              : `${scopeLabel("DEFAULT")} · ${summary(p)}`;
            return (
              <Button
                key={p.id}
                size="compact-xs"
                variant="light"
                color="yellow"
                onClick={() => navigate(`/my-billing/fee-policy/${p.id}?edit=1`)}>
                {label}
                <Box component="span" ml={6}>→</Box>
              </Button>
            );
          })}
        </Group>
      </Group>
    </Alert>
  );
}
